import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  IconButton,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Chip,
  Divider,
  Grid,
  Paper,
} from "@mui/material";
import {
  AddOutlined,
  DeleteOutlineOutlined,
  RemoveOutlined,
  CloseOutlined,
  LocalOfferOutlined,
  RestaurantOutlined,
  AccessTimeOutlined,
  InfoOutlined,
  WarningAmberOutlined,
  LocalFireDepartmentOutlined,
  AttachMoneyOutlined,
  CategoryOutlined,
  CheckCircleOutlined,
  CancelOutlined,
  DeliveryDiningOutlined,
  DinnerDiningOutlined,
  ShoppingBagOutlined,
  SettingsOutlined,
  TuneOutlined,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient, useQuery } from "react-query";
import { t } from "i18next";
import axios from "axios";
import {
  getLocalStorageMenuItem,
  SARIconThemeColor,
  setMobileBackColor,
  setMobileButttonBackColor,
  setMobileCardBorderColor,
  setMobileCardColor,
  setMobileTextColor,
} from "../../../utility";

import sar_for_white_theme from "../../../images/sar_for_white_theme.svg";
import no_image from "../../../images/No_Image_Available.jpg";

// Default placeholder image
const DEFAULT_IMAGE = "https://via.placeholder.com/400x300/e0e0e0/666666?text=No+Image+Available";

const Order = ({
  order,
  currency,
  tableQr,
  tableName,
  refetch,
  handleOpenToast,
  ordersLength,
  isDesktop
}) => {
  const queryString = window.location.search;
  const urlParam = new URLSearchParams(queryString);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const lang = getLocalStorageMenuItem("lang");
  const token = getLocalStorageMenuItem("gustToken");

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [imageError, setImageError] = useState(false);



  const fetchItemDetails = async () => {
    const timeZone = getLocalStorageMenuItem("timezone") || "";

    const { data: response } = await axios.get(
      `${process.env.REACT_APP_API}/menus/${tableQr}/item-details/${order.item_id}`,
      {
        headers: {
          localization: getLocalStorageMenuItem("lang") || "",
          timeZone: timeZone?.replace(/["]+/g, ''),
        },
      }
    );
    return response.data;
  };

  const { data: itemDetails, isLoading: isLoadingDetails } = useQuery(
    ["itemDetails", order.item_id],
    fetchItemDetails,
    {
      enabled: openDialog,
      onError: (res) =>
        handleOpenToast(t(res.response?.data?.message || "Error loading details"), false),
    }
  );

  const updateOrderField = async (field, values) => {
    const { data: response } = await axios.post(
      `${process.env.REACT_APP_API}/basket-items/${order.index}`,
      { ...values, token, menu_code: tableQr }
    );
    return response.data;
  };

  const { mutate: mutateOrderField, isLoading: isUpdatingOrder } = useMutation(
    (params) => updateOrderField(params.field, params.values),
    {
      onSuccess: () => refetch(),
      onError: (res) =>
        handleOpenToast(t(res.response?.data?.message || "Error"), false),
      onSettled: () => queryClient.invalidateQueries("orderUpdates"),
    }
  );

  const deleteOrderItem = async () => {
    const { data: response } = await axios.delete(
      `${process.env.REACT_APP_API}/basket-items/${order.index}?token=${token}&menu_code=${tableQr}`
    );
    return response.data;
  };

  const { mutate: deleteOrder, isLoading: isDeleting } = useMutation(
    deleteOrderItem,
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(["basket-items-count", getLocalStorageMenuItem("lang")]);

        queryClient.invalidateQueries(["basket-items", getLocalStorageMenuItem("lang")]);

        if (ordersLength === 1) {
          navigate(
            `/${tableName}/${tableQr}/sections?section=0&t=${urlParam.get(
              "t"
            )}&lang=${lang}`
          );
        } else {
          await refetch();
        }
      },
      onError: (res) =>
        handleOpenToast(t(res.response?.data?.message || "Error"), false),
      onSettled: () => queryClient.invalidateQueries("orderDeletion"),
    }
  );


  const renderCurrencyIcon = (size = 18) => {
    const currencyIcon = currency === "SAR" ? (
      <img
        width={size}
        height={size}
        src={sar_for_white_theme}
        style={{ marginLeft: 4 }}
        alt="SAR"
      />
    ) : (
      <span className="sub-font" style={{ fontSize: size - 4, marginLeft: 4 }}>{currency}</span>
    );

    return currencyIcon;
  }


  const handleOpenDialog = () => {
    setOpenDialog(true);
    setImageError(false);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl || imageError) return no_image;
    return imageUrl;
  };


  const renderItemDetailsDialog = () => {
    const details = itemDetails?.details;

    return (
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: "90vh",
          },
        }}
        className="alignByLang"
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
            backgroundColor: setMobileCardColor(),
            color: "white",
          }}
        >
          <Typography className="head-font" style={{ color: setMobileTextColor() }} variant="h5" fontWeight={700}>
            {t("Item Details")}
          </Typography>
          <IconButton onClick={handleCloseDialog} size="small" sx={{ color: "white" }}>
            <CloseOutlined />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {isLoadingDetails ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={8}>
              <CircularProgress size={60} />
            </Box>
          ) : details ? (
            <Box>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: isDesktop ? 500 : 250,
                  overflow: "hidden",
                  backgroundColor: "#f5f5f5",
                }}
              >
                <Box
                  component="img"
                  src={getImageUrl(details.image)}
                  alt={details.name}
                  onError={() => setImageError(true)}
                  sx={{
                    width: "100%",
                    height: "100%",
                    resizeMode: "contain",
                  }}
                />

                <Box
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                  }}
                >
                  {details.sold_out === 1 ? (
                    <Chip
                      className="sub-font"
                      icon={<CancelOutlined />}
                      label={t("Sold Out")}
                      color="error"
                      sx={{ fontWeight: 600 }}
                    />
                  ) : details.active ? (
                    <Chip
                      className="sub-font"
                      icon={<CheckCircleOutlined />}
                      label={t("Available")}
                      color="success"
                      sx={{ fontWeight: 600 }}
                    />
                  ) : (
                    <Chip
                      className="sub-font"
                      label={t("Unavailable")}
                      color="default"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Box>
              </Box>

              <Box p={3}>
                {/* Item Name & Label */}
                <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                  <Typography className="sub-font" variant="h4" fontWeight={700} flex={1}>
                    {details.name}
                  </Typography>
                  {details.label_name && (
                    <Chip
                      className="sub-font"
                      label={details.label_name}
                      color="primary"
                      icon={<LocalOfferOutlined />}
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Box>

                {/* Description */}
                {details.description && (
                  <Paper elevation={0} sx={{ p: 2, bgcolor: "#f8f9fa", borderRadius: 2, mb: 3 }}>
                    <Box display="flex" alignItems="flex-start" gap={1}>
                      <InfoOutlined sx={{ fontSize: 20, color: "primary.main", mt: 0.3 }} />
                      <Typography className="sub-font" variant="body1" color="text.secondary">
                        {details.description}
                      </Typography>
                    </Box>
                  </Paper>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Price Section */}
                <Box mb={3}>
                  <Typography className="head-font" variant="h6" fontWeight={700} mb={2} display="flex" alignItems="center" gap={1}>
                    <AttachMoneyOutlined color="primary" />
                    {t("Pricing Information")}
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Paper elevation={2} sx={{ p: 2, textAlign: "center", borderRadius: 2 }}>
                        <Typography className="sub-font" variant="caption" color="text.secondary" display="block" mb={1}>
                          {t("Current Price")}
                        </Typography>
                        <Box display="flex" alignItems="center" justifyContent="center">
                          <Typography className="sub-font" variant="h4" fontWeight={700} color="primary.main">
                            {details.price}
                          </Typography>
                          {renderCurrencyIcon(24)}
                        </Box>
                      </Paper>
                    </Grid>

                    {details.old_price > 0 && (
                      <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, textAlign: "center", borderRadius: 2, bgcolor: "#fef3f3" }}>
                          <Typography className="head-font" variant="caption" color="text.secondary" display="block" mb={1}>
                            {t("Previous Price")}
                          </Typography>
                          <Box display="flex" alignItems="center" justifyContent="center">
                            <Typography
                              className="sub-font"
                              variant="h5"
                              fontWeight={600}
                              sx={{ textDecoration: "line-through" }}
                              color="error.main"
                            >
                              {details.old_price}
                            </Typography>
                            {renderCurrencyIcon(20)}
                          </Box>
                        </Paper>
                      </Grid>
                    )}

                    {details.lowest_price_option && (
                      <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, textAlign: "center", borderRadius: 2, bgcolor: "#f0f7ff" }}>
                          <Typography className="sub-font" variant="caption" color="text.secondary" display="block" mb={1}>
                            {t("Lowest Price Option")}
                          </Typography>
                          <Box display="flex" alignItems="center" justifyContent="center">
                            <Typography className="sub-font" variant="h6" fontWeight={600} color="info.main">
                              {details.lowest_price_option}
                            </Typography>
                            {renderCurrencyIcon(18)}
                          </Box>
                        </Paper>
                      </Grid>
                    )}


                  </Grid>


                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Additional Information Grid */}
                <Grid container spacing={3}>
                  {/* Calories */}
                  {details.calories && (
                    <Grid item xs={12} sm={6}>
                      <Paper elevation={1} sx={{ p: 2, borderRadius: 2, bgcolor: "#fff8e1" }}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <LocalFireDepartmentOutlined color="warning" />
                          <Typography className="head-font" variant="subtitle1" fontWeight={600}>
                            {t("Calories")}
                          </Typography>
                        </Box>
                        <Typography className="sub-font" variant="h5" fontWeight={700} color="warning.dark">
                          {details.calories} kcal
                        </Typography>
                      </Paper>
                    </Grid>
                  )}

                  {/* Preparation Time */}
                  {(details.min_preparation_time || details.max_preparation_time) ? (
                    <Grid item xs={12} sm={6}>
                      <Paper elevation={1} sx={{ p: 2, borderRadius: 2, bgcolor: "#e8f5e9" }}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <AccessTimeOutlined color="success" />
                          <Typography className="head-font" variant="subtitle1" fontWeight={600}>
                            {t("Preparation Time")}
                          </Typography>
                        </Box>
                        <Typography className="sub-font" variant="h6" fontWeight={700} color="success.dark">
                          {details.min_preparation_time || 0} - {details.max_preparation_time || 0} {t("min")}
                        </Typography>
                      </Paper>
                    </Grid>
                  ) : null}
                </Grid>

                {/* Service Availability */}
                <Box mb={3}>
                  <Typography className="head-font" variant="h6" fontWeight={700} mb={2} display="flex" alignItems="center" gap={1}>
                    <CategoryOutlined color="primary" />
                    {t("Service Availability")}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Paper
                        elevation={details.is_delivery_on ? 3 : 0}
                        sx={{
                          p: 2,
                          textAlign: "center",
                          borderRadius: 2,
                          bgcolor: details.is_delivery_on ? "#e3f2fd" : "#f5f5f5",
                          opacity: details.is_delivery_on ? 1 : 0.5,
                        }}
                      >
                        <DeliveryDiningOutlined
                          sx={{ fontSize: 40, color: details.is_delivery_on ? "primary.main" : "grey.400" }}
                        />
                        <Typography className="sub-font" variant="body2" fontWeight={600} mt={1}>
                          {t("Delivery")}
                        </Typography>
                        {details.is_delivery_on && (
                          <CheckCircleOutlined sx={{ fontSize: 18, color: "success.main", mt: 0.5 }} />
                        )}
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper
                        elevation={details.is_pickup_on ? 3 : 0}
                        sx={{
                          p: 2,
                          textAlign: "center",
                          borderRadius: 2,
                          bgcolor: details.is_pickup_on ? "#f3e5f5" : "#f5f5f5",
                          opacity: details.is_pickup_on ? 1 : 0.5,
                        }}
                      >
                        <ShoppingBagOutlined
                          sx={{ fontSize: 40, color: details.is_pickup_on ? "secondary.main" : "grey.400" }}
                        />
                        <Typography className="sub-font" variant="body2" fontWeight={600} mt={1}>
                          {t("Pickup")}
                        </Typography>
                        {details.is_pickup_on && (
                          <CheckCircleOutlined sx={{ fontSize: 18, color: "success.main", mt: 0.5 }} />
                        )}
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper
                        elevation={details.is_dinein_on ? 3 : 0}
                        sx={{
                          p: 2,
                          textAlign: "center",
                          borderRadius: 2,
                          bgcolor: details.is_dinein_on ? "#fff3e0" : "#f5f5f5",
                          opacity: details.is_dinein_on ? 1 : 0.5,
                        }}
                      >
                        <DinnerDiningOutlined
                          sx={{ fontSize: 40, color: details.is_dinein_on ? "warning.main" : "grey.400" }}
                        />
                        <Typography className="sub-font" variant="body2" fontWeight={600} mt={1}>
                          {t("Dine In")}
                        </Typography>
                        {details.is_dinein_on && (
                          <CheckCircleOutlined sx={{ fontSize: 18, color: "success.main", mt: 0.5 }} />
                        )}
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>

                {/* Warnings */}
                {details?.warnings?.length > 0 && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Box mb={3}>
                      <Typography
                        className="head-font"
                        variant="h6"
                        fontWeight={700}
                        mb={2}
                        display="flex"
                        alignItems="center"
                        gap={1}
                        color="error.main"
                      >
                        <WarningAmberOutlined />
                        {t("Important Warnings")}
                      </Typography>

                      <Grid container spacing={2}>
                        {details.warnings.map((warning) => (
                          <Grid item xs={12} sm={6} md={4} key={warning.id}>
                            <Paper
                              elevation={2}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                p: 2,
                                bgcolor: "#ffebee",
                                borderLeft: "4px solid",
                                borderColor: "error.main",
                                borderRadius: 2,
                                transition: "transform 0.2s ease",
                                "&:hover": { transform: "scale(1.02)" },
                              }}
                            >
                              <Box
                                component="img"
                                src={warning.icon}
                                alt={warning.name}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  objectFit: "contain",
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                className="sub-font"
                                variant="body1"
                                fontWeight={600}
                                color="error.dark"
                                sx={{ flex: 1 }}
                              >
                                {warning.name}
                              </Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </>
                )}

                {/* Modifiers */}
                {details?.modifiers?.length > 0 && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Box mb={3}>
                      <Typography
                        className="head-font"
                        variant="h6"
                        fontWeight={700}
                        mb={2}
                        display="flex"
                        alignItems="center"
                        gap={1}
                        color="primary.main"
                      >
                        <TuneOutlined />
                        {t("Available Modifiers")}
                      </Typography>

                      {details.modifiers.map((modifierGroup) => (
                        <Box key={modifierGroup.group.id} mb={3}>
                          <Typography
                            className="sub-font"
                            variant="subtitle1"
                            fontWeight={700}
                            mb={1}
                            sx={{ display: "flex", alignItems: "center", gap: 1 }}
                          >
                            <SettingsOutlined sx={{ fontSize: 20, color: "primary.main" }} />
                            {modifierGroup.group.name}

                          </Typography>

                          <Grid container spacing={2}>
                            {modifierGroup.order_options.map((option) => (
                              <Grid item xs={12} sm={6} md={4} key={option.id}>
                                <Paper
                                  elevation={1}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    p: 2,
                                    borderRadius: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    transition: "background 0.2s ease",
                                    "&:hover": {
                                      backgroundColor: "action.hover",
                                    },
                                  }}
                                >
                                  <Typography className="sub-font" variant="body1" fontWeight={600}>
                                    {option.name}
                                  </Typography>
                                  {option.price && (
                                    <Typography
                                      className="sub-font"
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ fontWeight: 500 }}
                                    >
                                      +{option.price} {renderCurrencyIcon(14)}
                                    </Typography>
                                  )}
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>

                        </Box>
                      ))}
                    </Box>
                  </>
                )}

                {/* Price Options */}
                {details.has_price_options && details.price_options?.length > 0 && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Box mb={3}>
                      <Typography className="head-font" variant="h6" fontWeight={700} mb={2}>
                        {t("Price Options")}
                      </Typography>
                      <Grid container spacing={2}>
                        {details.price_options.map((option, index) => (
                          <Grid item xs={12} sm={6} key={index}>
                            <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                              <Typography className="sub-font" variant="subtitle1" fontWeight={600}>
                                {option.name}
                              </Typography>
                              <Box display="flex" alignItems="center" mt={1}>
                                <Typography className="sub-font" variant="h6" fontWeight={700} color="primary">
                                  {option.price}
                                </Typography>
                                {renderCurrencyIcon(18)}
                              </Box>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </>
                )}

                {/* Price Option Items */}
                {details.has_price_option_item && details.price_option_items?.length > 0 && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Box mb={3}>
                      <Typography className="head-font" variant="h6" fontWeight={700} mb={2}>
                        {t("Price Options")}
                      </Typography>
                      <Grid container spacing={2}>
                        {details.price_option_items.map((optionItem, index) => (
                          <Grid item xs={12} sm={6} md={4} key={index}>
                            <Paper
                              elevation={2}
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                transition: "0.2s ease",
                                "&:hover": { transform: "scale(1.02)" },
                              }}
                            >
                              <Typography className="sub-font" variant="subtitle1" fontWeight={600}>
                                {optionItem.name}
                              </Typography>

                              <Box display="flex" alignItems="center" mt={1}>
                                <Typography className="sub-font" variant="h6" fontWeight={700} color="primary">
                                  {optionItem.price}
                                </Typography>
                                {renderCurrencyIcon(18)}
                              </Box>

                              {optionItem.old_price > 0 && (
                                <Typography
                                  className="sub-font"
                                  variant="body2"
                                  sx={{
                                    textDecoration: "line-through",
                                    color: "error.main",
                                    mt: 0.5,
                                  }}
                                >
                                  {optionItem.old_price} {renderCurrencyIcon(14)}
                                </Typography>
                              )}

                              {optionItem.calories && (
                                <Box
                                  className="sub-font"
                                  display="flex"
                                  alignItems="center"
                                  gap={0.5}
                                  mt={1}
                                  color="text.secondary"
                                >
                                  <LocalFireDepartmentOutlined fontSize="small" color="warning" />
                                  <Typography variant="body2">
                                    {optionItem.calories} kcal
                                  </Typography>
                                </Box>
                              )}
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </>
                )}


                {/* Recommended Items */}
                {details.recommended_items?.length > 0 && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Box mb={3}>
                      <Typography className="head-font" variant="h6" fontWeight={700} mb={2}>
                        {t("You May Also Like")}
                      </Typography>
                      <Grid container spacing={2}>
                        {details.recommended_items.map((item) => (
                          <Grid item xs={6} sm={4} key={item.id}>
                            <Paper elevation={2} sx={{ p: 1.5, borderRadius: 2, textAlign: "center" }}>
                              <Box
                                component="img"
                                src={getImageUrl(item.image)}
                                alt={item.name}
                                onError={() => setImageError(true)}
                                sx={{
                                  width: "100%",
                                  height: 100,
                                  objectFit: "cover",
                                  borderRadius: 2,
                                  mb: 1,
                                }}
                              />

                              <Typography className="sub-font" variant="body2" fontWeight={600} noWrap>
                                {item.name}
                              </Typography>
                              <Box display="flex" alignItems="center" justifyContent="center" mt={1}>
                                <Typography className="sub-font" variant="subtitle2" color="primary" fontWeight={700}>
                                  {item.price}
                                </Typography>
                                {renderCurrencyIcon(14)}
                              </Box>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </>
                )}

                {/* Combo Items */}
                {details.is_combo_item && details.comboItems?.length > 0 && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Box mb={3}>
                      <Typography className="head-font" variant="h6" fontWeight={700} mb={2}>
                        {t("Combo Includes")}
                      </Typography>
                      <Stack spacing={1}>
                        {details.comboItems.map((item, index) => (
                          <Paper key={index} elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                            <Typography className="sub-font" variant="body1" fontWeight={600}>
                              {item.name}
                            </Typography>
                          </Paper>
                        ))}
                      </Stack>
                    </Box>
                  </>
                )}


              </Box>
            </Box>
          ) : (
            <Box py={8} textAlign="center">
              <InfoOutlined sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                {t("No details available")}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  /** =======================
   *  UI
   * ======================= */
  if (order.is_order_option && order.is_combo_child) return null;

  return (
    <>
      <Box
        mt={2}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          border: `1px solid ${setMobileCardBorderColor()}`,
          borderRadius: 2,
          backgroundColor: setMobileCardColor(),
          p: isDesktop ? 3 : 2,
          gap: isDesktop ? 4 : 2,
          alignItems: isDesktop ? "center" : "flex-start",
          transition: "all 0.25s ease",
        }}
      >
        {/* === LEFT SIDE === */}
        <Box sx={{ flex: 1 }}>
          {/* Item Name - Clickable */}
          <Typography
            className="sub-font"
            variant={isDesktop ? "h6" : "subtitle1"}
            sx={{
              fontWeight: 600,
              color: setMobileTextColor(),
              cursor: "pointer",
              "&:hover": {
                color: setMobileButttonBackColor(),
                textDecoration: "underline",
              },
              transition: "all 0.2s ease",
            }}
            onClick={handleOpenDialog}
          >
            {order.name}
          </Typography>

          {/* Price */}
          <Typography
            className="sub-font"
            variant="subtitle1"
            sx={{
              mt: 1,
              fontWeight: 600,
              color: setMobileTextColor(),
              fontSize: isDesktop ? "1.1rem" : "1rem",
            }}
          >
            {order.price} {renderCurrencyIcon()}
          </Typography>

          {/* Options */}
          {order.options?.length > 0 && (
            <Box mt={1}>
              {order.options.map((option) => (
                <Typography
                  className="sub-font"
                  key={option.index}
                  variant="body2"
                  sx={{ color: setMobileTextColor(), fontSize: isDesktop ? 14 : 13 }}
                >
                  • {option.name} × {option.quantity} – {option.price}{" "}
                  {renderCurrencyIcon()}
                </Typography>
              ))}
            </Box>
          )}

          {/* Notes */}
          <TextField
            className="sub-font"
            defaultValue={order.notes}
            onBlur={(e) =>
              mutateOrderField({
                field: "notes",
                values: { quantity: null, notes: e.target.value },
              })
            }
            placeholder={t("Enter your note")}
            variant="outlined"
            size="small"
            fullWidth
            sx={{
              mt: 1.5,
              "& .MuiOutlinedInput-root": {
                color: setMobileTextColor(),
                backgroundColor: setMobileCardColor(),
                "& fieldset": {
                  borderColor: setMobileCardBorderColor(),
                },
                "&.Mui-focused fieldset": {
                  borderColor: setMobileButttonBackColor(),
                },
              },
            }}
          />
        </Box>

        {/* === RIGHT SIDE === */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            position: "relative",
            justifyContent: "space-between",
            minWidth: isDesktop ? 160 : "auto",
          }}
        >
          {/* Delete Button */}
          <IconButton
            size="small"
            disabled={isDeleting}
            onClick={deleteOrder}
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              width: 32,
              height: 32,
              backgroundColor: "rgba(255,255,255,0.9)",
              border: `1px solid ${setMobileCardBorderColor()}`,
              boxShadow: 2,
              transition: "all 0.25s ease",
              "&:hover": {
                backgroundColor: "error.main",
                color: "#fff",
                transform: "scale(1.05)",
              },
            }}
          >
            {isDeleting ? (
              <CircularProgress size={16} sx={{ color: "error.main" }} />
            ) : (
              <DeleteOutlineOutlined fontSize="small" />
            )}
          </IconButton>

          {/* Product Image - Clickable with fallback */}
          <Box
            component="img"
            src={order.image || no_image}
            alt={order.name}
            onClick={handleOpenDialog}
            onError={(e) => {
              e.target.src = no_image;
            }}
            sx={{
              width: isDesktop ? 140 : 100,
              height: isDesktop ? 140 : 100,
              borderRadius: 2,
              objectFit: "cover",
              border: `1px solid ${setMobileCardBorderColor()}`,
              cursor: "pointer",
              transition: "all 0.25s ease",
              "&:hover": {
                transform: "scale(1.05)",
                boxShadow: 4,
              },
            }}
          />

          {/* Quantity Controls */}
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              mt: 1.5,
              backgroundColor: "#fff",
              borderRadius: "999px",
              boxShadow: 2,
              px: isDesktop ? 2 : 1,
              py: 0.5,
              transform: isDesktop ? "scale(1.1)" : "scale(1)",
              transition: "all 0.25s ease",
            }}
          >
            <IconButton
              size="small"
              disabled={order.quantity <= 1 || isUpdatingOrder}
              onClick={() =>
                mutateOrderField({
                  field: "quantity",
                  values: { quantity: order.quantity - 1, notes: "" },
                })
              }
            >
              {isUpdatingOrder ? (
                <CircularProgress size={14} />
              ) : (
                <RemoveOutlined fontSize="small" sx={{ color: "error.main" }} />
              )}
            </IconButton>

            <Typography
              className="sub-font"
              sx={{
                mx: 1,
                fontWeight: 600,
                fontSize: isDesktop ? "1.1rem" : "1rem",
              }}
            >
              {order.quantity}
            </Typography>

            <IconButton
              size="small"
              disabled={order.quantity >= 100 || isUpdatingOrder}
              onClick={() =>
                mutateOrderField({
                  field: "quantity",
                  values: { quantity: order.quantity + 1, notes: "" },
                })
              }
            >
              {isUpdatingOrder ? (
                <CircularProgress size={14} />
              ) : (
                <AddOutlined fontSize="small" color="primary" />
              )}
            </IconButton>
          </Stack>
        </Box>
      </Box>

      {/* Item Details Dialog */}
      {renderItemDetailsDialog()}
    </>
  );
};

export default Order;